import * as React from "react";
import { Text, makeStyles } from "@fluentui/react-components";
import type {
  ConditionDraft,
  ConditionGroupDraft,
  QueryEntityMetadata,
  FetchXmlBuilderTree,
  IFetchXmlBuilderDataSource,
  JoinDraft,
  QueryRelationDefinition,
  QueryTarget,
} from "./types.js";
import {
  buildAlias,
  getDescendantGroupIds,
  getParentMetadataForJoin,
  newConditionDraft,
  newConditionGroup,
  newJoinDraft,
} from "./components/query-builder/fetchXmlBuilderHelpers.js";
import {
  canAssignRelationKeyForParent,
  getAvailableManyToOneRelationsForParent,
} from "./components/query-builder/queryBuilderRelationGuards.js";
import { JoinsSection } from "./components/query-builder/sections/JoinsSection.js";
import { ManyToOneRelationPickerDialogControl } from "./components/dialogs/ManyToOneRelationPickerDialogControl.js";

export interface QueryBuilderControlProps {
  dataSource: IFetchXmlBuilderDataSource | null;
  tree?: FetchXmlBuilderTree | null;
  onTreeChange?: (tree: FetchXmlBuilderTree | null) => void;
}

const useStyles = makeStyles({
  root: {
    display: "grid",
    gap: "12px",
    gridTemplateColumns: "1fr",
  },
  errorText: {
    color: "var(--colorPaletteRedForeground1)",
  },
});

function collectJoinDescendants(joins: JoinDraft[], seedIds: string[]): string[] {
  const descendants = new Set<string>();
  const queue = [...seedIds];

  while (queue.length > 0) {
    const id = queue.shift();
    if (!id || descendants.has(id)) {
      continue;
    }

    descendants.add(id);
    joins.filter((item) => item.parentTarget === id).forEach((item) => queue.push(item.id));
  }

  return Array.from(descendants);
}

interface PendingManyToOneSelection {
  parentTarget: QueryTarget;
  attributeName: string;
  parentLabel: string;
  attributeLabel: string;
  relations: QueryRelationDefinition[];
}

function findRelationForJoin(parentMetadata: QueryEntityMetadata | null, join: JoinDraft): QueryRelationDefinition | undefined {
  if (!parentMetadata) {
    return undefined;
  }

  if (join.relationKey) {
    const byKey = parentMetadata.relations.find((item) => item.key === join.relationKey);
    if (byKey) {
      return byKey;
    }
  }

  return parentMetadata.relations.find(
    (item) =>
      item.targetEntityLogicalName === join.targetEntityLogicalName &&
      item.fromAttribute === join.fromAttribute &&
      item.toAttribute === join.toAttribute,
  );
}

export function QueryBuilderControl({ dataSource, tree, onTreeChange }: QueryBuilderControlProps): JSX.Element {
  const styles = useStyles();

  const [rootEntityLogicalName, setRootEntityLogicalName] = React.useState<string>(tree?.rootEntityLogicalName || "");
  const [rootMetadata, setRootMetadata] = React.useState<QueryEntityMetadata | null>(null);
  const [rootMetadataError, setRootMetadataError] = React.useState<string>("");
  const [rootSelectedAttributes, setRootSelectedAttributes] = React.useState<string[]>(tree?.rootSelectedAttributes || []);

  const [joins, setJoins] = React.useState<JoinDraft[]>(tree?.joins || []);
  const [joinMetadataByJoinId, setJoinMetadataByJoinId] = React.useState<Record<string, QueryEntityMetadata | undefined>>({});
  const [joinValidationMessageById, setJoinValidationMessageById] = React.useState<Record<string, string>>({});
  const [showEntityFilters, setShowEntityFilters] = React.useState(true);
  const [pendingManyToOneSelection, setPendingManyToOneSelection] = React.useState<PendingManyToOneSelection | null>(null);

  const [conditionGroups, setConditionGroups] = React.useState<ConditionGroupDraft[]>(tree?.conditionGroups || []);
  const [conditions, setConditions] = React.useState<ConditionDraft[]>(tree?.conditions || []);

  const applyRelationToJoin = React.useCallback(
    (join: JoinDraft, relationKey: string, currentJoins: JoinDraft[]): JoinDraft => {
      const nextJoin: JoinDraft = { ...join, relationKey };

      if (!rootMetadata) {
        return nextJoin;
      }

      const parentMetadata = getParentMetadataForJoin(nextJoin, rootMetadata, joinMetadataByJoinId);
      const relation = parentMetadata?.relations.find((item) => item.key === relationKey);
      if (!relation) {
        return nextJoin;
      }

      const usedAliases = new Set(currentJoins.filter((item) => item.id !== join.id).map((item) => item.alias));
      const alias = nextJoin.alias || buildAlias(relation.targetEntityLogicalName, usedAliases);

      return {
        ...nextJoin,
        alias,
        targetEntityLogicalName: relation.targetEntityLogicalName,
        fromAttribute: relation.fromAttribute,
        toAttribute: relation.toAttribute,
      };
    },
    [joinMetadataByJoinId, rootMetadata],
  );

  React.useEffect(() => {
    if (!tree) {
      setRootEntityLogicalName("");
      setRootSelectedAttributes([]);
      setJoins([]);
      setJoinMetadataByJoinId({});
      setJoinValidationMessageById({});
      setConditionGroups([]);
      setConditions([]);
      setPendingManyToOneSelection(null);
      setShowEntityFilters(true);
      return;
    }

    setRootEntityLogicalName(tree.rootEntityLogicalName);
    setRootSelectedAttributes(tree.rootSelectedAttributes);
    setJoins(tree.joins);
    setConditionGroups(tree.conditionGroups);
    setConditions(tree.conditions);
  }, [tree]);

  React.useEffect(() => {
    setRootMetadata(null);
    setRootMetadataError("");
    setJoinMetadataByJoinId({});

    if (!dataSource || !rootEntityLogicalName) {
      return;
    }

    let disposed = false;

    dataSource
      .getEntityMetadata(rootEntityLogicalName)
      .then((metadata) => {
        if (disposed) return;
        setRootMetadata(metadata);
      })
      .catch((error) => {
        if (disposed) return;
        setRootMetadataError(String(error));
      });

    return () => {
      disposed = true;
    };
  }, [dataSource, rootEntityLogicalName]);

  React.useEffect(() => {
    if (!rootMetadata) {
      return;
    }

    if (conditionGroups.length === 0) {
      setConditionGroups([newConditionGroup("root", null)]);
      setConditions([]);
    }
  }, [conditionGroups.length, rootMetadata]);

  React.useEffect(() => {
    if (!rootMetadata) {
      return;
    }

    setJoins((current) => {
      let hasChanges = false;

      const next = current.map((join) => {
        const parentMetadata = getParentMetadataForJoin(join, rootMetadata, joinMetadataByJoinId);
        const relation = findRelationForJoin(parentMetadata, join);
        if (!relation) return join;

        const usedAliases = new Set(current.filter((item) => item.id !== join.id).map((item) => item.alias));
        const nextAlias = join.alias || buildAlias(relation.targetEntityLogicalName, usedAliases);
        const nextRelationKey = join.relationKey || relation.key;

        if (
          join.relationKey === nextRelationKey &&
          join.targetEntityLogicalName === relation.targetEntityLogicalName &&
          join.fromAttribute === relation.fromAttribute &&
          join.toAttribute === relation.toAttribute &&
          join.alias === nextAlias
        ) {
          return join;
        }

        hasChanges = true;
        return {
          ...join,
          relationKey: nextRelationKey,
          alias: nextAlias,
          targetEntityLogicalName: relation.targetEntityLogicalName,
          fromAttribute: relation.fromAttribute,
          toAttribute: relation.toAttribute,
        };
      });

      return hasChanges ? next : current;
    });
  }, [joinMetadataByJoinId, rootMetadata]);

  React.useEffect(() => {
    if (!dataSource) {
      return;
    }

    joins.forEach((join) => {
      if (!join.targetEntityLogicalName) return;
      if (Object.prototype.hasOwnProperty.call(joinMetadataByJoinId, join.id)) return;

      dataSource
        .getEntityMetadata(join.targetEntityLogicalName)
        .then((metadata) => {
          setJoinMetadataByJoinId((current) => ({ ...current, [join.id]: metadata }));
        })
        .catch((error) => {
          setJoinMetadataByJoinId((current) => ({ ...current, [join.id]: undefined }));
          setRootMetadataError((prev) => (prev ? `${prev}\n${String(error)}` : String(error)));
        });
    });
  }, [dataSource, joinMetadataByJoinId, joins]);

  const currentTree = React.useMemo<FetchXmlBuilderTree | null>(() => {
    if (!rootEntityLogicalName) {
      return null;
    }

    return {
      rootEntityLogicalName,
      rootSelectedAttributes,
      joins,
      conditionGroups,
      conditions,
    };
  }, [conditionGroups, conditions, joins, rootEntityLogicalName, rootSelectedAttributes]);

  React.useEffect(() => {
    onTreeChange?.(currentTree);
  }, [currentTree, onTreeChange]);

  const addJoinForRelation = React.useCallback(
    (parentTarget: QueryTarget, relationKey: string) => {
      setJoins((current) => {
        if (current.some((join) => join.parentTarget === parentTarget && join.relationKey === relationKey)) {
          return current;
        }

        const join = applyRelationToJoin(newJoinDraft(parentTarget), relationKey, current);
        return [...current, join];
      });
    },
    [applyRelationToJoin],
  );

  const onToggleRootAttribute = React.useCallback((attributeName: string, checked: boolean) => {
    setRootSelectedAttributes((current) => {
      if (checked) {
        if (current.includes(attributeName)) return current;
        return [...current, attributeName];
      }
      return current.filter((item) => item !== attributeName);
    });

    if (!rootMetadata) {
      return;
    }

    const matchingManyToOneRelations = rootMetadata.relations.filter(
      (relation) => relation.relationType === "many-to-one" && relation.toAttribute === attributeName,
    );
    const availableManyToOneRelations = getAvailableManyToOneRelationsForParent(joins, "root", matchingManyToOneRelations);

    if (checked) {
      if (availableManyToOneRelations.length === 1) {
        addJoinForRelation("root", availableManyToOneRelations[0].key);
      }

      if (availableManyToOneRelations.length > 1) {
        const attributeLabel =
          rootMetadata.attributes.find((attribute) => attribute.logicalName === attributeName)?.displayName || attributeName;

        setPendingManyToOneSelection({
          parentTarget: "root",
          attributeName,
          parentLabel: rootMetadata.displayName || rootMetadata.logicalName,
          attributeLabel,
          relations: availableManyToOneRelations,
        });
      }
    }

    if (matchingManyToOneRelations.length > 0 && !checked) {
      setPendingManyToOneSelection((current) => {
        if (current?.parentTarget === "root" && current.attributeName === attributeName) {
          return null;
        }

        return current;
      });

      const relationKeys = new Set(matchingManyToOneRelations.map((relation) => relation.key));
      setJoins((current) => {
        const joinIdsToRemove = current
          .filter((join) => join.parentTarget === "root" && relationKeys.has(join.relationKey))
          .map((join) => join.id);

        if (joinIdsToRemove.length < 1) {
          return current;
        }

        const descendants = collectJoinDescendants(current, joinIdsToRemove);
        const descendantSet = new Set(descendants);

        setConditionGroups((groups) => groups.filter((group) => !descendantSet.has(String(group.target))));
        setConditions((conds) => conds.filter((item) => !descendantSet.has(String(item.target))));
        setJoinMetadataByJoinId((metadata) => {
          const next = { ...metadata };
          descendants.forEach((id) => delete next[id]);
          return next;
        });

        return current.filter((join) => !descendantSet.has(join.id));
      });
    }
  }, [addJoinForRelation, joins, rootMetadata]);

  const onToggleJoinAttribute = React.useCallback((joinId: string, attributeName: string, checked: boolean) => {
    setJoins((current) =>
      current.map((join) => {
        if (join.id !== joinId) return join;
        if (checked) {
          if (join.selectedAttributes.includes(attributeName)) return join;
          return { ...join, selectedAttributes: [...join.selectedAttributes, attributeName] };
        }
        return { ...join, selectedAttributes: join.selectedAttributes.filter((item) => item !== attributeName) };
      }),
    );

    const joinMetadata = joinMetadataByJoinId[joinId];
    if (!joinMetadata) {
      return;
    }

    const matchingManyToOneRelations = joinMetadata.relations.filter(
      (relation) => relation.relationType === "many-to-one" && relation.toAttribute === attributeName,
    );
    const availableManyToOneRelations = getAvailableManyToOneRelationsForParent(joins, joinId, matchingManyToOneRelations);

    if (checked) {
      if (availableManyToOneRelations.length === 1) {
        addJoinForRelation(joinId, availableManyToOneRelations[0].key);
      }

      if (availableManyToOneRelations.length > 1) {
        const attributeLabel =
          joinMetadata.attributes.find((attribute) => attribute.logicalName === attributeName)?.displayName || attributeName;

        setPendingManyToOneSelection({
          parentTarget: joinId,
          attributeName,
          parentLabel: joinMetadata.displayName || joinMetadata.logicalName,
          attributeLabel,
          relations: availableManyToOneRelations,
        });
      }
    }

    if (matchingManyToOneRelations.length > 0 && !checked) {
      setPendingManyToOneSelection((current) => {
        if (current?.parentTarget === joinId && current.attributeName === attributeName) {
          return null;
        }

        return current;
      });

      const relationKeys = new Set(matchingManyToOneRelations.map((relation) => relation.key));
      setJoins((current) => {
        const joinIdsToRemove = current
          .filter((join) => join.parentTarget === joinId && relationKeys.has(join.relationKey))
          .map((join) => join.id);

        if (joinIdsToRemove.length < 1) {
          return current;
        }

        const descendants = collectJoinDescendants(current, joinIdsToRemove);
        const descendantSet = new Set(descendants);

        setConditionGroups((groups) => groups.filter((group) => !descendantSet.has(String(group.target))));
        setConditions((conds) => conds.filter((item) => !descendantSet.has(String(item.target))));
        setJoinMetadataByJoinId((metadata) => {
          const next = { ...metadata };
          descendants.forEach((id) => delete next[id]);
          return next;
        });

        return current.filter((join) => !descendantSet.has(join.id));
      });
    }
  }, [addJoinForRelation, joinMetadataByJoinId, joins]);

  const onCloseManyToOnePicker = React.useCallback(() => {
    setPendingManyToOneSelection(null);
  }, []);

  const onConfirmManyToOnePicker = React.useCallback(
    (relationKey: string) => {
      setPendingManyToOneSelection((current) => {
        if (!current) {
          return current;
        }

        addJoinForRelation(current.parentTarget, relationKey);
        return null;
      });
    },
    [addJoinForRelation],
  );

  const onAddJoin = React.useCallback(
    (parentTarget: QueryTarget, relationKey?: string) => {
      setJoins((current) => {
        if (relationKey && !canAssignRelationKeyForParent(current, parentTarget, relationKey)) {
          return current;
        }

        const join = relationKey
          ? applyRelationToJoin(newJoinDraft(parentTarget), relationKey, current)
          : newJoinDraft(parentTarget);
        return [...current, join];
      });
    },
    [applyRelationToJoin],
  );

  const onRemoveJoin = React.useCallback((joinId: string) => {
    setJoins((current) => {
      const descendants = collectJoinDescendants(current, [joinId]);
      const descendantSet = new Set(descendants);

      setConditionGroups((groups) => groups.filter((group) => !descendants.includes(String(group.target))));
      setConditions((conds) => conds.filter((item) => !descendants.includes(String(item.target))));
      setJoinMetadataByJoinId((metadata) => {
        const next = { ...metadata };
        descendants.forEach((id) => delete next[id]);
        return next;
      });
      setPendingManyToOneSelection((pending) => {
        if (!pending || pending.parentTarget === "root") {
          return pending;
        }

        return descendantSet.has(pending.parentTarget) ? null : pending;
      });
      setJoinValidationMessageById((messages) => {
        const next = { ...messages };
        descendants.forEach((id) => delete next[id]);
        return next;
      });

      return current.filter((item) => !descendants.includes(item.id));
    });
  }, []);

  const onChangeJoin = React.useCallback(
    (joinId: string, update: Partial<JoinDraft>) => {
      setJoins((current) =>
        current.map((join) => {
          if (join.id !== joinId) return join;
          const next = { ...join, ...update };

          if (update.relationKey && !canAssignRelationKeyForParent(current, next.parentTarget, update.relationKey, joinId)) {
            setJoinValidationMessageById((messages) => ({
              ...messages,
              [joinId]: "This relation is already added for the selected parent.",
            }));
            return join;
          }

          if (Object.prototype.hasOwnProperty.call(update, "relationKey")) {
            setJoinValidationMessageById((messages) => {
              if (!messages[joinId]) {
                return messages;
              }

              const nextMessages = { ...messages };
              delete nextMessages[joinId];
              return nextMessages;
            });
          }

          if (update.parentTarget && update.parentTarget !== join.parentTarget) {
            next.relationKey = "";
            next.selectedAttributes = [];
            next.targetEntityLogicalName = "";
            next.fromAttribute = "";
            next.toAttribute = "";
          }

          if (update.relationKey) {
            const hydrated = applyRelationToJoin(next, update.relationKey, current);
            hydrated.selectedAttributes = [];
            return hydrated;
          }

          return next;
        }),
      );
    },
    [applyRelationToJoin],
  );

  const onDismissJoinValidationMessage = React.useCallback((joinId: string) => {
    setJoinValidationMessageById((messages) => {
      if (!messages[joinId]) {
        return messages;
      }

      const nextMessages = { ...messages };
      delete nextMessages[joinId];
      return nextMessages;
    });
  }, []);

  const onAddTopLevelGroup = React.useCallback((target: QueryTarget) => {
    setConditionGroups((current) => [...current, newConditionGroup(target, null)]);
  }, []);

  const onAddSubGroup = React.useCallback((groupId: string) => {
    setConditionGroups((current) => {
      const parent = current.find((item) => item.id === groupId);
      if (!parent) return current;
      return [...current, newConditionGroup(parent.target, groupId)];
    });
  }, []);

  const onRemoveGroup = React.useCallback((groupId: string) => {
    setConditionGroups((current) => {
      const ids = new Set(getDescendantGroupIds(groupId, current));
      setConditions((conds) => conds.filter((item) => !ids.has(item.groupId)));
      return current.filter((item) => !ids.has(item.id));
    });
  }, []);

  const onChangeGroup = React.useCallback((groupId: string, update: Partial<ConditionGroupDraft>) => {
    setConditionGroups((current) => current.map((group) => (group.id === groupId ? { ...group, ...update } : group)));
  }, []);

  const onAddCondition = React.useCallback((groupId: string) => {
    setConditionGroups((groups) => {
      const group = groups.find((item) => item.id === groupId);
      if (!group) return groups;
      setConditions((current) => [...current, newConditionDraft(group.id, group.target)]);
      return groups;
    });
  }, []);

  const onRemoveCondition = React.useCallback((conditionId: string) => {
    setConditions((current) => current.filter((item) => item.id !== conditionId));
  }, []);

  const onChangeCondition = React.useCallback((conditionId: string, update: Partial<ConditionDraft>) => {
    setConditions((current) =>
      current.map((condition) => {
        if (condition.id !== conditionId) return condition;
        return { ...condition, ...update };
      }),
    );
  }, []);

  if (!dataSource) {
    return <Text>No datasource configured yet.</Text>;
  }

  return (
    <div className={styles.root}>
      {rootMetadataError ? <Text className={styles.errorText}>{rootMetadataError}</Text> : null}

      {rootMetadata ? (
        <JoinsSection
          rootMetadata={rootMetadata}
          rootSelectedAttributes={rootSelectedAttributes}
          joins={joins}
          joinMetadataByJoinId={joinMetadataByJoinId}
          joinValidationMessageById={joinValidationMessageById}
          conditionGroups={conditionGroups}
          conditions={conditions}
          showFilters={showEntityFilters}
          onToggleRootAttribute={onToggleRootAttribute}
          onToggleShowFilters={setShowEntityFilters}
          onAddJoin={onAddJoin}
          onRemoveJoin={onRemoveJoin}
          onChangeJoin={onChangeJoin}
          onToggleJoinAttribute={onToggleJoinAttribute}
          onAddTopLevelGroup={onAddTopLevelGroup}
          onAddCondition={onAddCondition}
          onAddSubGroup={onAddSubGroup}
          onRemoveGroup={onRemoveGroup}
          onChangeGroup={onChangeGroup}
          onChangeCondition={onChangeCondition}
          onRemoveCondition={onRemoveCondition}
          onDismissJoinValidationMessage={onDismissJoinValidationMessage}
        />
      ) : null}

      <ManyToOneRelationPickerDialogControl
        open={Boolean(pendingManyToOneSelection)}
        parentLabel={pendingManyToOneSelection?.parentLabel || ""}
        attributeLabel={pendingManyToOneSelection?.attributeLabel || ""}
        relations={pendingManyToOneSelection?.relations || []}
        onConfirm={onConfirmManyToOnePicker}
        onClose={onCloseManyToOnePicker}
      />
    </div>
  );
}
